// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Hook for fetching the organization's latest locked period and each currency's locked initial true-up.
 * Uses React Query for data fetching and caching.
 */
import { getInitialTrueUps } from '@/accounting/api/get-initial-true-ups';
import { InitialTrueUpsForAllCurrencies } from '@/accounting/api/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Result type for the useInitialTrueUps hook.
 */
interface UseInitialTrueUpsResult {
  initialTrueUps: InitialTrueUpsForAllCurrencies | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/** Fetches the organization's latest locked period and each currency's locked initial true-up. */
export function useInitialTrueUps(): UseInitialTrueUpsResult {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['accounting', 'initial-true-ups'],
    queryFn: async () => {
      const response = await getInitialTrueUps();
      return response.initialTrueUps;
    },
  });

  return {
    initialTrueUps: data ?? null,
    isLoading,
    isError,
    error,
  };
}
